'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Pencil } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { typographyVariants } from '@/shared/ui/shadcn/typography';

type HeadingLevel = 'h1' | 'h2' | 'h3';

interface InlineEditableHeadingProps {
  value: string | null | undefined;
  onSave: (value: string | null) => Promise<void>;
  level: HeadingLevel;
  placeholder?: string;
  className?: string;
  showEditIcon?: boolean;
  maxLength?: number;
  textAlign?: 'left' | 'center' | 'right';
  disabled?: boolean;
}

export function InlineEditableHeading({
  value,
  onSave,
  level = 'h2',
  placeholder: placeholderProp,
  className,
  showEditIcon = true,
  maxLength,
  textAlign = 'left',
  disabled = false,
}: InlineEditableHeadingProps) {
  const tCommon = useTranslations('common');
  const placeholder = placeholderProp ?? tCommon('edit');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editableRef = useRef<HTMLDivElement>(null);
  const originalValueRef = useRef<string>(value || '');

  useEffect(() => {
    if (isEditing && editableRef.current) {
      // Focus and select all text
      editableRef.current.focus();
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editableRef.current);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (!editableRef.current || isSaving) return;

    setIsSaving(true);
    try {
      const newValue = editableRef.current.textContent?.trim() || null;
      await onSave(newValue);
      originalValueRef.current = newValue || '';
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      // Restore original value
      if (editableRef.current) {
        editableRef.current.textContent = originalValueRef.current;
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Restore original value
      if (editableRef.current) {
        editableRef.current.textContent = originalValueRef.current;
      }
      setIsEditing(false);
    }
  };

  const handleInput = () => {
    // Enforce max length if specified
    if (maxLength && editableRef.current) {
      const text = editableRef.current.textContent || '';
      if (text.length > maxLength) {
        // Prevent further input
        editableRef.current.textContent = text.slice(0, maxLength);
        // Move cursor to end
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(text));
    selection.collapseToEnd();
  };

  // Get heading-specific styling using Typography variants
  const getHeadingStyles = (level: HeadingLevel) => {
    // Use smaller font size for h2 (concept title)
    if (level === 'h2') {
      return 'scroll-m-20 text-section-title';
    }
    return typographyVariants({ variant: level });
  };

  const HeadingComponent = level;
  const displayValue = value || '';

  // Get alignment class
  const getAlignmentClass = () => {
    switch (textAlign) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  const handleClick = () => {
    if (!isEditing && !disabled) {
      originalValueRef.current = value || '';
      setIsEditing(true);
    }
  };

  return (
    <div
      className={cn(
        'group inline-flex items-center gap-2 rounded-md px-3 py-1 min-w-0',
        'transition-all duration-200',
        !disabled && 'cursor-pointer',
        'hover:bg-accent/50',
        isEditing && 'bg-accent/50',
        className
      )}
      onClick={handleClick}
    >
      {/* Add compensating spacer for edit icon balance */}
      {showEditIcon && <div className="w-4 h-4 opacity-0" aria-hidden="true" />}
      <HeadingComponent
        className={cn(
          getHeadingStyles(level),
          getAlignmentClass(),
          'flex-1 min-w-0 break-words scroll-m-0'
        )}
      >
        <div
          ref={editableRef}
          contentEditable={isEditing}
          suppressContentEditableWarning={true}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onPaste={handlePaste}
          className={cn(
            'outline-none',
            !value && !isEditing && 'text-muted-foreground italic'
          )}
          style={{
            wordBreak: 'break-word',
          }}
        >
          {!value && !isEditing ? placeholder : displayValue}
        </div>
      </HeadingComponent>
      {showEditIcon && !disabled && (
        <Pencil
          className={cn(
            'h-4 w-4 text-muted-foreground transition-all duration-200',
            'opacity-0 group-hover:opacity-100',
            'touch:opacity-100', // Always visible on touch devices
            isEditing && 'opacity-100'
          )}
        />
      )}
    </div>
  );
}
